<script setup>
import VueDatePicker from '@vuepic/vue-datepicker'
import { computed, ref, defineEmits, onMounted, nextTick } from 'vue'
import { formatDate, formatDateNumeric } from '@/Composables/common.js'

const props = defineProps({
	placeHolder: {
		type: String,
		default: '',
		required: false,
	},
	minDate: {
		type: Date,
		default: null,
		required: false,
	},
	maxDate: {
		type: Date,
		default: null,
		required: false,
	},
	minYear: {
		type: Number,
		default: new Date().getFullYear() - 100,
		required: false,
	},
	maxYear: {
		type: Number,
		default: new Date().getFullYear() + 20,
		required: false,
	},
	preventMinMaxNavigation: {
		type: Boolean,
		default: true,
		required: false,
	},
	startDate: {
		type: Date,
		default: null,
		required: false,
	},
	position: {
		type: String,
		default: 'center',
		required: false,
	},
	autoPosition: {
		type: String,
		default: 'bottom',
		required: false,
	},
	error: {
		type: [String, Boolean],
		default: false,
		required: false,
	},
	showError: {
		type: Boolean,
		default: true,
		required: false,
	},
	showLabel: {
		type: Boolean,
		default: false,
		required: false,
	},
	showErrorAsLabel: {
		type: Boolean,
		default: false,
		required: false,
	},
	allowTextInput: {
		type: Boolean,
		default: false,
		required: false,
	},
	allowMonthSelection: {
		type: Boolean,
		default: false,
		required: false,
	},
	labelName: {
		type: String,
		default: 'Date',
		required: false,
	},
	fancyFormat: {
		type: Boolean,
		default: false,
		required: false,
	},
	highlightDate: {
		type: Date,
		default: null,
		required: false,
	},
	modelValue: {
		type: [String, Date],
		default: null,
		required: false,
	},
	multiCalendar: {
		type: Boolean,
		default: false,
		required: false,
	},
})

const highlight = computed(() => {
	return props.highlightDate instanceof Date
		? {
				dates: [props.highlightDate],
				options: { highlightDisabled: true },
			}
		: null
})

const fieldId = computed(() => props.id ?? props.name)

const isReady = ref(false)

const markers = ref([
	{
		type: 'dot',
		date: new Date(),
	},
])

onMounted(() => {
	markers.value = [
		{
			type: 'dot',
			date: new Date(),
		},
	]
	nextTick(() => {
		isReady.value = true
	})
})

const isOpened = ref(false)

function toggleCaret() {
	isOpened.value = !isOpened.value
}

const dateFormat = (e) => {
	if (!props.fancyFormat) {
		let formatedDate = formatDateNumeric(e)
		return formatedDate
	} else {
		let formatedDate = formatDate(e)
		return formatedDate
	}
}

const textInputOptions = computed(() => {
	if (props.allowTextInput) {
		return { format: 'dd/MM/yyyy' }
	} else {
		return false
	}
})

const showInput = computed(() => {
	if (!textInputOptions.value) {
		return false
	}
	return true
})

const yearRange = computed(() => {
	return [props.minYear, props.maxYear]
})

const emit = defineEmits(['onSelect'])

const date = ref()
const handleDate = (dateValue) => {
	date.value = dateValue
	emit('onSelect')
}
const datepickerRef = ref()

const handleSelectDate = (dateValue) => {
	datepickerRef.value.selectDate(dateValue)
}

const handleInput = (e) => {
	const input = e.target

	let value = input.value.replace(/[^0-9]/g, '') // Remove non-numeric characters

	if (e.key !== 'Backspace' && e.key !== 'Delete') {
		// Only format auto-slash when it's not backspace/delete
		// Add slashes for date formatting
		if (value.length > 1) {
			value = value.slice(0, 2) + '/' + value.slice(2)
		}
		if (value.length > 4) {
			value = value.slice(0, 5) + '/' + value.slice(5)
		}

		// Update the input value
		input.value = value.slice(0, 10) // Limit to 10 characters (DD/MM/YYYY)
	}
}

defineExpose({ handleDate, handleSelectDate })
</script>

<template>
	<div class="relative w-full __dob">
		<VueDatePicker
			v-if="isReady"
			ref="datepickerRef"
			v-bind="$attrs"
			v-model="date"
			:model-value="date"
			required
			no-today
			auto-apply
			:ui="{
				menu: allowMonthSelection ? '' : '__single',
				input: showLabel ? 'px-3 pt-6' : 'px-3',
			}"
			:text-input="textInputOptions"
			:position="position"
			:auto-position="autoPosition"
			:placeholder="placeHolder"
			:min-date="minDate"
			:max-date="maxDate"
			:year-range="yearRange"
			:prevent-min-max-navigation="preventMinMaxNavigation"
			:start-date="startDate"
			:format="dateFormat"
			:markers="markers"
			:highlight="highlight"
			:month-change-on-scroll="false"
			:clearable="false"
			:enable-time-picker="false"
			:teleport="true"
			:multi-calendars="multiCalendar"
			:class="[
				error
					? 'border border-idrive-hover border-opacity-80'
					: 'border-gray-200',
			]"
			@open="toggleCaret"
			@closed="toggleCaret"
			@update:model-value="handleDate"
		>
			<template
				v-if="showInput"
				#dp-input="{ value, onInput, onEnter, onClear, onTab, onBlur }"
			>
				<input
					type="text"
					placeholder="DD/MM/YYYY"
					class="dp__input pt-6 rounded"
					:value="value"
					@input="onInput"
					@keydown.tab="onTab"
					@keydown.enter="onEnter"
					@keyup="handleInput"
					@clear="onClear"
					@blur="onBlur"
				/>
			</template>
		</VueDatePicker>
		<label
			v-if="showLabel"
			:for="fieldId"
			class="pointer-events-none absolute top-3.5 text-xs text-gray-600 origin-top-left -translate-y-1/2 px-3 duration-300"
		>
			{{
				typeof error === 'string' && error && showErrorAsLabel
					? error
					: labelName
			}}
		</label>
		<div class="w-full">
			<span
				class="multiselect-caret absolute right-1 top-[1.4rem] block transition-all"
				:class="isOpened ? 'rotate-90' : ''"
				aria-hidden="true"
			/>
		</div>
		<div
			v-if="error && showError"
			class="mt-1.5 flex flex-row justify-start"
		>
			<div class="text-sm leading-none opacity-70 text-idrive-hover">
				{{ error }}
			</div>
		</div>
	</div>
</template>
